import { cn } from "@repo/design-system/utils";
import { Link } from "@tanstack/react-router";
import { Check, ChevronRight } from "lucide-react";
import { Fragment, memo } from "react";

export type CheckoutProgressStep = "cart" | "checkout" | "complete";

export interface CheckoutSubStep {
  /** Current sub-step within the checkout flow */
  current: "information" | "payment";
  /** Selected cart IDs, forwarded to sub-step links so navigation preserves the selection */
  cartIds: string[];
}

export interface CheckoutProgressProps {
  currentStep: CheckoutProgressStep;
  /** Appends the current sub-step (e.g. "Shipping info") after the Checkout label */
  checkoutSubStep?: CheckoutSubStep;
}

const STEPS: { id: CheckoutProgressStep; label: string }[] = [
  { id: "cart", label: "Cart" },
  { id: "checkout", label: "Checkout" },
  { id: "complete", label: "Order Sent" }
];

const SUB_STEP_LABELS: Record<CheckoutSubStep["current"], string> = {
  information: "Shipping info",
  payment: "Payment"
};

type StepStatus = "done" | "current" | "upcoming";

/**
 * Breadcrumb-style progress row for the Cart -> Checkout -> Order Complete
 * flow, with a numbered/checkmark circle per step. Shared across CartPage,
 * CheckoutPage, and OrderConfirmationPage.
 */
export const CheckoutProgress = memo(function CheckoutProgress({
  currentStep,
  checkoutSubStep
}: CheckoutProgressProps) {
  const currentIndex = STEPS.findIndex((s) => s.id === currentStep);

  return (
    <nav
      className="flex items-center gap-2 text-sm"
      aria-label="Checkout progress"
    >
      {STEPS.map((step, index) => {
        const status: StepStatus =
          index < currentIndex
            ? "done"
            : index === currentIndex
              ? "current"
              : "upcoming";

        // const label =
        //   step.id === "checkout" && status === "current" && checkoutSubStep
        //     ? `${step.label} · ${SUB_STEP_LABELS[checkoutSubStep.current]}`
        //     : step.label;

        const label = step.label;

        return (
          <Fragment key={step.id}>
            <StepItem
              status={status}
              number={index + 1}
              label={label}
              isCartStep={step.id === "cart"}
            />
            {index < STEPS.length - 1 && (
              <ChevronRight
                className="h-4 w-4 text-gray-300"
                aria-hidden="true"
              />
            )}
          </Fragment>
        );
      })}
    </nav>
  );
});

interface StepItemProps {
  status: StepStatus;
  number: number;
  label: string;
  isCartStep: boolean;
}

function StepItem({ status, number, label, isCartStep }: StepItemProps) {
  // A finished Cart step is the pinned way back to the cart, so it has to read
  // as tappable: the link colour rather than the muted grey of the other done
  // steps.
  const isLink = isCartStep && status === "done";

  const content = (
    <span className="inline-flex items-center gap-1.5">
      <span
        className={cn(
          "flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full text-[10px] font-bold",
          status === "done" && "bg-gray-700 text-white",
          status === "current" && "bg-purple-600 text-white",
          status === "upcoming" && "bg-gray-200 text-gray-500"
        )}
      >
        {status === "done" ? (
          <Check className="h-3 w-3" aria-hidden="true" />
        ) : (
          number
        )}
      </span>
      <span
        className={cn(
          status === "current" && "font-semibold text-purple-600",
          status === "done" && !isLink && "text-gray-500",
          isLink && "font-medium text-purple-600 group-hover:text-purple-700",
          status === "upcoming" && "text-gray-400"
        )}
      >
        {label}
      </span>
    </span>
  );

  if (isLink) {
    return (
      // `min-h-11` with matching negative margins gives a 44px tap target
      // without moving the breadcrumb or growing the pinned header.
      <Link
        to="/cart"
        className="group -my-3 inline-flex min-h-11 items-center rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500"
      >
        {content}
      </Link>
    );
  }

  return (
    <span aria-current={status === "current" ? "step" : undefined}>
      {content}
    </span>
  );
}
